import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Upload, ScanLine, Loader2, CheckCircle, X, FileImage, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { casesAPI } from '../lib/api';

const FIELD_LABELS = {
  name: 'Full Name',
  dob: 'Date of Birth',
  country: 'Country',
  email: 'Email',
  telephone: 'Phone',
  address: 'Address',
  a_number: 'A-Number',
  uscis_receipt: 'USCIS Receipt',
  type_of_service: 'Service Type',
  emergency_contact_name: 'Emergency Contact',
  emergency_contact_phone: 'Emergency Contact Phone',
  employer_name: 'Employer',
  employer_address: 'Employer Address',
  notes: 'Notes'
};

// Safely convert any value to a displayable string
function toDisplayString(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(toDisplayString).join(', ');
  if (typeof value === 'object') {
    try { return JSON.stringify(value); } catch { return '[complex data]'; }
  }
  return String(value);
}

export default function FormScanner({ onDataExtracted }) {
  const [scanning, setScanning] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [formType, setFormType] = useState('');
  const [fileName, setFileName] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [scanError, setScanError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, WEBP image or PDF file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    setFileName(file.name);
    setScanError('');
    if (file.type.startsWith('image/')) {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }

    setScanning(true);
    setExtractedData(null);

    try {
      const response = await casesAPI.scanForm(file);
      if (response.data.success) {
        setExtractedData(response.data.extracted_data);
        setFormType(response.data.form_type || 'Unknown');
        toast.success(`Form scanned successfully — ${response.data.form_type || 'Immigration Form'} detected`);
      } else {
        const errMsg = response.data.error || 'Could not extract data from this form';
        setScanError(errMsg);
        toast.error(errMsg);
      }
    } catch (error) {
      const errMsg = error.response?.data?.detail || 'Form scanning failed. Please try again.';
      // Check for budget exceeded error
      if (errMsg.toLowerCase().includes('budget')) {
        setScanError('AI service budget exceeded. Please add balance to your Universal Key (Profile → Universal Key → Add Balance).');
      } else {
        setScanError(errMsg);
      }
      toast.error(errMsg);
    } finally {
      setScanning(false);
    }
  };

  const handleApplyData = () => {
    if (extractedData && onDataExtracted) {
      const cleaned = {};
      Object.entries(extractedData).forEach(([k, v]) => {
        if (!v || k === 'form_type') return;
        if (typeof v === 'string' || Array.isArray(v)) {
          cleaned[k] = v;
        }
      });
      onDataExtracted(cleaned);
      toast.success('Form data applied to fields');
    }
  };

  const handleReset = () => {
    setExtractedData(null);
    setFormType('');
    setFileName('');
    setPreviewUrl(null);
    setScanError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const nonEmptyFields = extractedData
    ? Object.entries(extractedData).filter(([k, v]) => v && k !== 'form_type')
    : [];

  return (
    <Card data-testid="form-scanner" className="border-dashed">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
          <ScanLine className="w-5 h-5 text-primary" />
          Scan Immigration Form
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!extractedData && !scanning && (
          <div className="text-center py-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="form-scanner-input"
            />
            <FileImage className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-body text-sm text-muted-foreground mb-3">
              Upload a scanned immigration form to auto-extract data
            </p>
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              data-testid="form-scanner-upload-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Form
            </Button>
            <p className="font-body text-xs text-muted-foreground mt-2">
              Supports JPEG, PNG, WEBP, PDF (max 10MB)
            </p>
            {scanError && (
              <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-left">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="font-body text-sm text-red-700 dark:text-red-400">{scanError}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {scanning && (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 text-primary mx-auto mb-3 animate-spin" />
            <p className="font-body font-medium text-foreground">Scanning {fileName}...</p>
            <p className="font-body text-sm text-muted-foreground mt-1">
              AI is analyzing the form and extracting data
            </p>
          </div>
        )}

        {extractedData && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-body font-medium text-green-800 dark:text-green-400">
                  {nonEmptyFields.length} fields extracted
                </span>
                {formType && formType !== 'Unknown' && (
                  <Badge variant="secondary" className="text-xs">{formType}</Badge>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={handleReset}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {previewUrl && (
              <div className="rounded-lg overflow-hidden border max-h-32">
                <img src={previewUrl} alt="Scanned form" className="w-full h-32 object-cover" />
              </div>
            )}

            <div className="bg-muted/50 rounded-lg p-3 max-h-48 overflow-y-auto space-y-1.5">
              {nonEmptyFields.map(([key, value]) => (
                <div key={key} className="flex items-start gap-2 text-sm">
                  <span className="font-body font-medium text-muted-foreground min-w-[120px]">
                    {FIELD_LABELS[key] || key}:
                  </span>
                  <span className="font-body text-foreground">{toDisplayString(value)}</span>
                </div>
              ))}
              {nonEmptyFields.length === 0 && (
                <div className="flex items-center gap-2 text-amber-600">
                  <AlertCircle className="w-4 h-4" />
                  <span className="font-body text-sm">No data could be extracted from this form</span>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleApplyData}
                disabled={nonEmptyFields.length === 0}
                className="flex-1"
                data-testid="form-scanner-apply-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Apply to Fields
              </Button>
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                <Upload className="w-4 h-4 mr-2" />
                Scan Another
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
